import { Directive, computed, input } from '@angular/core';
import { cva, type VariantProps } from 'class-variance-authority';

import * as fromHelpers from '../helpers';

export enum AvatarColorVariant {
  light = 'light',
  dark = 'dark',
  neutral = 'neutral',
}

export enum AvatarSizeVariant {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export const avatarVariants = cva(
  fromHelpers.cn(
    'flex items-center justify-center rounded-full hover:cursor-pointer overflow-hidden relative font-semibold',
    '[&>svg]:text-icon-neutral-enabled [&>img]:absolute [&>img]:inset-0 [&>img]:size-full [&>img]:object-cover',
  ),
  {
    variants: {
      color: {
        [`${AvatarColorVariant.light}`]: 'bg-primary-100 hover:bg-primary-200 text-primary-500',
        [`${AvatarColorVariant.dark}`]: 'bg-transparency-white-30 hover:bg-transparency-white-15 text-shades-white',
        [`${AvatarColorVariant.neutral}`]: 'bg-avatar-neutral-enabled hover:bg-avatar-neutral-hovered text-shades-white',
      },
      size: {
        [`${AvatarSizeVariant.xs}`]: 'min-w-24 size-24 typo-footer [&>svg]:size-16',
        [`${AvatarSizeVariant.sm}`]: 'min-w-32 size-32 typo-caption [&>svg]:size-20',
        [`${AvatarSizeVariant.md}`]: 'min-w-40 size-40 typo-p3 [&>svg]:size-24',
        [`${AvatarSizeVariant.lg}`]: 'min-w-48 size-48 typo-p2 [&>svg]:size-32',
        [`${AvatarSizeVariant.xl}`]: 'min-w-56 size-56 typo-p1 [&>svg]:size-40',
      },
    },
    defaultVariants: {
      color: AvatarColorVariant.neutral,
      size: AvatarSizeVariant.md,
    },
  },
);
export type AvatarVariants = VariantProps<typeof avatarVariants>;

@Directive({
  selector: '[dmcomAvatar]',
  standalone: true,
  host: {
    '[class]': 'className()',
  },
})
export class AvatarDirective {
  readonly color = input<AvatarVariants['color']>(AvatarColorVariant.neutral);
  readonly size = input<AvatarVariants['size']>(AvatarSizeVariant.md);

  protected readonly className = computed(() => {
    return fromHelpers.cn(
      avatarVariants({
        color: this.color(),
        size: this.size(),
      }),
    );
  });
}
